import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Avatar,
  AvatarFallback,
  AvatarImage
} from '../ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import {
  Shield,
  Edit,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';
import { getInitials } from '../../lib/utils';
import { resolveAvatarUrl } from '../../lib/avatar';

function LeaderCard({
  leader,
  getRoleDisplayName,
  onEdit,
  canEdit
}) {
  const roleDisplay =
    getRoleDisplayName(
      leader
    );

  return (
    <Card className="rounded-lg border-[rgb(var(--tn-border-ffffff))]/[0.065] bg-[rgb(var(--tn-bg-151a20))] shadow-none transition-colors hover:border-[rgb(var(--tn-border-ffffff))]/[0.12] hover:bg-[rgb(var(--tn-bg-191f26))]">

      <CardContent className="p-2.5">

        <div className="flex min-w-0 items-center gap-2.5">

          <Avatar className="h-10 w-10 shrink-0 border border-[rgb(var(--tn-border-ffffff))]/[0.09] bg-[rgb(var(--tn-bg-20262d))]">

            {leader.avatar_url && (
              <AvatarImage
                src={resolveAvatarUrl(
                  leader.avatar_url
                )}
                alt={
                  leader.name ||
                  'Ledare'
                }
                className="object-cover"
              />
            )}

            <AvatarFallback className="bg-[rgb(var(--tn-bg-20262d))] text-[12px] font-semibold text-[var(--t-accent)]">
              {getInitials(
                leader.name
              )}
            </AvatarFallback>

          </Avatar>

          <div className="min-w-0 flex-1">

            <p
              className="truncate text-[13px] font-semibold leading-4 text-[rgb(var(--tn-text-f2f4f7))]"
              title={leader.name}
            >
              {leader.name}
            </p>

            <div className="mt-1 flex min-w-0 items-center gap-1.5">

              <Shield className="h-3.5 w-3.5 shrink-0 text-[var(--t-accent)]" />

              <span className="truncate text-[10.5px] text-[rgb(var(--tn-text-8c96a1))]">
                {roleDisplay}
              </span>

            </div>

          </div>

          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 rounded-md text-[rgb(var(--tn-text-8d97a3))] hover:bg-[rgb(var(--tn-tint-ffffff))]/[0.05] hover:text-[rgb(var(--tn-text-ffffff))]"
              data-testid={`edit-member-${leader.id}`}
              onClick={() =>
                onEdit(
                  leader
                )
              }
            >
              <Edit className="h-3.5 w-3.5" />
            </Button>
          )}

        </div>

      </CardContent>

    </Card>
  );
}

export default function LeadersList({
  leaders,
  loading,
  sortOption,
  onSortChange,
  getRoleDisplayName,
  onEditMember,
  canEdit,
  showSectionTitle = true
}) {
  const { t } = useTranslation(
    'team_admin'
  );

  const LEADER_SORT_OPTIONS = [
    {
      id: 'role',
      label: t(
        'sort.role_head_first'
      ),
      icon: ArrowUpDown
    },
    {
      id: 'name_asc',
      label: t(
        'sort.name_asc'
      ),
      icon: ArrowUp
    },
    {
      id: 'name_desc',
      label: t(
        'sort.name_desc'
      ),
      icon: ArrowDown
    }
  ];

  const SortIcon =
    LEADER_SORT_OPTIONS.find(
      (option) =>
        option.id === sortOption
    )?.icon ||
    ArrowUpDown;

  if (loading) {
    return (
      <section className="tn-panel overflow-hidden rounded-xl border border-[rgb(var(--tn-border-ffffff))]/[0.07] bg-[rgb(var(--tn-bg-12161b))] shadow-none">

        <div className={`tn-section-header flex min-h-[52px] items-center border-b border-[rgb(var(--tn-border-ffffff))]/[0.07] px-3.5 ${
          showSectionTitle ? 'justify-between' : 'justify-end'
        }`}>

          {showSectionTitle && (
            <div className="flex items-center gap-2.5">
              <Skeleton className="h-7 w-7 rounded-lg" />
              <Skeleton className="h-5 w-24" />
            </div>
          )}

          <Skeleton className="h-8 w-[205px] max-w-full" />

        </div>

        <div className="grid gap-2 p-2.5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">

          {[
            1,
            2,
            3,
            4
          ].map(
            (item) => (
              <Skeleton
                key={item}
                className="h-[62px] rounded-lg"
              />
            )
          )}

        </div>

      </section>
    );
  }

  return (
    <section className="tn-panel overflow-hidden rounded-xl border border-[rgb(var(--tn-border-ffffff))]/[0.07] bg-[rgb(var(--tn-bg-12161b))] shadow-none">

      {/* Toolbar. The Trupp header already carries the active tab label. */}
      <div className={`tn-section-header flex min-h-[52px] flex-col gap-2 border-b border-[rgb(var(--tn-border-ffffff))]/[0.07] bg-[rgb(var(--tn-bg-12161b))] px-3 py-2 sm:flex-row sm:items-center ${
        showSectionTitle ? 'sm:justify-between' : 'sm:justify-end'
      }`}>

        {showSectionTitle && (
          <div className="flex items-center gap-2.5">

            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--tn-accent-fill)]/10 text-[var(--t-accent)]">
              <Shield className="h-4 w-4" />
            </div>

            <h2 className="tn-section-title text-[14px] font-semibold text-[rgb(var(--tn-text-f2f4f7))]">
              {t(
                'roster.leaders'
              )}
            </h2>

            <span className="flex h-5 min-w-5 items-center justify-center rounded-md bg-[rgb(var(--tn-tint-ffffff))]/[0.055] px-1.5 text-[10px] font-semibold tabular-nums text-[rgb(var(--tn-text-c5ccd4))]">
              {leaders.length}
            </span>

          </div>
        )}

        <Select
          value={sortOption}
          onValueChange={
            onSortChange
          }
        >
          <SelectTrigger
            className="tn-control h-8 w-[205px] rounded-lg border-[rgb(var(--tn-border-ffffff))]/[0.08] bg-[rgb(var(--tn-bg-0f1318))] px-2.5 text-[11px] shadow-none"
            data-testid="leader-sort-select"
          >
            <SortIcon className="mr-1.5 h-3.5 w-3.5 text-[rgb(var(--tn-text-7f8995))]" />
            <SelectValue />
          </SelectTrigger>

          <SelectContent>

            {LEADER_SORT_OPTIONS.map(
              (option) => {
                const Icon =
                  option.icon;

                return (
                  <SelectItem
                    key={option.id}
                    value={option.id}
                  >
                    <div className="flex items-center gap-2">

                      <Icon className="h-3.5 w-3.5" />

                      {option.label}

                    </div>
                  </SelectItem>
                );
              }
            )}

          </SelectContent>

        </Select>

      </div>

      {/* Leaders */}
      <div className="p-2.5">

        {leaders.length > 0 ? (
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">

            {leaders.map(
              (leader) => (
                <LeaderCard
                  key={leader.id}
                  leader={leader}
                  getRoleDisplayName={
                    getRoleDisplayName
                  }
                  onEdit={
                    onEditMember
                  }
                  canEdit={
                    canEdit
                  }
                />
              )
            )}

          </div>
        ) : (
          <div className="py-7 text-center text-[12px] text-[rgb(var(--tn-text-8d97a3))]">
            {t(
              'roster.no_leaders'
            )}
          </div>
        )}

      </div>

    </section>
  );
}